import React, { useEffect, useMemo, useRef, useState } from 'react';
import { themeColor } from '../../utils/themeColor';

/*
 * A sample "My dashboard" shown behind the Pro lock on the welcome page: the
 * kind of dashboard a Pro user builds from their own accounts, with made-up
 * numbers that tick so it feels live. Purely decorative (aria-hidden).
 */

// Small seeded random so the sample looks the same on every visit.
function rng(seed) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const START = 118000;
const POSITIONS = [
  { sym: 'NVDA', qty: 120, price: 181.4, color: '#76b900' },
  { sym: 'AAPL', qty: 85, price: 247.1, color: '#a3a3a3' },
  { sym: 'TSLA', qty: 40, price: 412.8, color: '#e31937' },
  { sym: 'BTC', qty: 0.42, price: 104250, color: '#f7931a' },
  { sym: 'MNQ', qty: 2, price: 21480, get color() { return themeColor('#667eea'); } },
];
const ALLOC = [
  { label: 'Schwab', pct: 0.42, color: '#38bdf8' },
  { label: 'Robinhood', pct: 0.24, color: '#ccff00' },
  { label: 'Coinbase', pct: 0.18, color: '#3b82f6' },
  { label: 'NinjaTrader', pct: 0.16, color: '#f97316' },
];
const TRADES = [
  ['MNQ', 'Long', '+$412'], ['NVDA', 'Buy', '+$238'], ['TSLA', 'Sell', '-$96'], ['BTC', 'Buy', '+$184'],
  ['AAPL', 'Sell', '+$71'], ['MNQ', 'Short', '+$265'], ['ETH', 'Buy', '-$42'], ['SPY', 'Buy', '+$128'],
];

const money = (n, sign = false) => {
  const s = Math.abs(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  if (!sign) return n < 0 ? `-${s}` : s;
  return `${n >= 0 ? '+' : '-'}${s}`;
};

function useLive(enabled) {
  const [tick, setTick] = useState(0);
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!enabled || !visible || reduced) return undefined;
    const id = window.setInterval(() => setTick((t) => t + 1), 1600);
    return () => window.clearInterval(id);
  }, [enabled, visible]);
  return [ref, tick];
}

export default function DashboardPreview({ live = true }) {
  const [ref, tick] = useLive(live);

  // 90 days of equity, gently up with real-looking dips.
  const base = useMemo(() => {
    const r = rng(7);
    const pts = [];
    let v = START;
    for (let i = 0; i < 90; i += 1) {
      v += (r() - 0.42) * 900 + (i > 55 ? 120 : 0);
      pts.push(v);
    }
    return pts;
  }, []);
  // The latest point wanders a little each tick, like an open day.
  const wobble = useMemo(() => {
    const r = rng(100 + tick);
    return (r() - 0.45) * 700;
  }, [tick]);
  const series = useMemo(() => [...base.slice(0, -1), base[base.length - 1] + wobble], [base, wobble]);
  const value = series[series.length - 1];
  const today = value - series[series.length - 2];
  const peak = Math.max(...series);
  let ddPeak = -Infinity;
  let maxDd = 0;
  series.forEach((v) => { ddPeak = Math.max(ddPeak, v); maxDd = Math.min(maxDd, (v - ddPeak) / ddPeak); });

  const W = 520;
  const H = 150;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const x = (i) => (i / (series.length - 1)) * W;
  const y = (v) => H - 8 - ((v - min) / (max - min || 1)) * (H - 20);
  const line = series.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;

  const cal = useMemo(() => {
    const r = rng(42);
    return Array.from({ length: 25 }, () => {
      const p = (r() - 0.38) * 900;
      return Math.abs(p) < 60 ? 0 : p;
    });
  }, []);
  const calMax = Math.max(...cal.map(Math.abs));

  const prices = useMemo(() => {
    const r = rng(500 + tick);
    return POSITIONS.map((p) => ({ ...p, now: p.price * (1 + (r() - 0.48) * 0.006) }));
  }, [tick]);

  let angle = 0;
  const donut = ALLOC.map((a) => {
    const start = angle;
    angle += a.pct * 360;
    return { ...a, start, end: angle };
  });
  const arc = (start, end, r = 34, cx = 44, cy = 44) => {
    const toXY = (deg) => [cx + r * Math.cos(((deg - 90) * Math.PI) / 180), cy + r * Math.sin(((deg - 90) * Math.PI) / 180)];
    const [x1, y1] = toXY(start);
    const [x2, y2] = toXY(end - 1.5);
    return `M${x1},${y1} A${r},${r} 0 ${end - start > 180 ? 1 : 0} 1 ${x2},${y2}`;
  };

  return (
    <div className="wo-dp" ref={ref} aria-hidden="true">
      <div className="wo-dp-card wo-dp-kpi" style={{ '--i': 0 }}>
        <span>Account value</span>
        <b>{money(value)}</b>
        <em className="is-up">▲ {money(value - START, true)} · 90 days</em>
      </div>
      <div className="wo-dp-card wo-dp-kpi" style={{ '--i': 1 }}>
        <span>Today’s P&amp;L</span>
        <b key={Math.round(today)} className={`wo-dp-flash ${today >= 0 ? 'is-up' : 'is-down'}`}>{money(today, true)}</b>
        <em>{(today / value * 100).toFixed(2)}% · 5 accounts</em>
      </div>
      <div className="wo-dp-card wo-dp-kpi" style={{ '--i': 2 }}>
        <span>Winning days</span>
        <div className="wo-dp-ring-row">
          <svg viewBox="0 0 36 36" className="wo-dp-ring"><circle cx="18" cy="18" r="15" /><circle cx="18" cy="18" r="15" className="is-fill" style={{ strokeDasharray: `${0.62 * 94.2} 94.2` }} /></svg>
          <b>62%</b>
        </div>
        <em>34 up · 21 down</em>
      </div>
      <div className="wo-dp-card wo-dp-kpi" style={{ '--i': 3 }}>
        <span>Max drawdown</span>
        <b className="is-down">{(maxDd * 100).toFixed(1)}%</b>
        <div className="wo-dp-bar"><i style={{ width: `${Math.min(100, Math.abs(maxDd) * 1000)}%` }} /></div>
      </div>

      <div className="wo-dp-card wo-dp-equity" style={{ '--i': 4 }}>
        <div className="wo-dp-head">
          <strong>Equity</strong>
          <div className="wo-dp-pills"><i>1M</i><i className="is-on">3M</i><i>1Y</i><i>All</i></div>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="wo-dp-chart">
          <defs>
            <linearGradient id="wo-dp-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity="0.38" />
              <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill="url(#wo-dp-fill)" className="wo-dp-area" />
          <path d={line} fill="none" stroke="#22c55e" strokeWidth="2.2" className="wo-dp-line" vectorEffect="non-scaling-stroke" />
          <line x1="0" x2={W} y1={y(peak)} y2={y(peak)} className="wo-dp-peak" vectorEffect="non-scaling-stroke" />
          <circle cx={x(series.length - 1)} cy={y(value)} r="4" className="wo-dp-dot" />
        </svg>
      </div>

      <div className="wo-dp-card wo-dp-cal" style={{ '--i': 5 }}>
        <div className="wo-dp-head"><strong>P&amp;L calendar</strong><span>October</span></div>
        <div className="wo-dp-cal-grid">
          {['M', 'T', 'W', 'T', 'F'].map((d, i) => <small key={`${d}${i}`}>{d}</small>)}
          {cal.map((p, i) => {
            const a = p ? 0.18 + (Math.abs(p) / calMax) * 0.6 : 0;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <i key={i} className={i === 23 ? 'is-today' : ''} style={{ background: p ? `rgba(${p > 0 ? '34,197,94' : '239,68,68'},${a})` : undefined, '--d': `${i * 30}ms` }} />
            );
          })}
        </div>
      </div>

      <div className="wo-dp-card wo-dp-alloc" style={{ '--i': 6 }}>
        <div className="wo-dp-head"><strong>Allocation</strong></div>
        <div className="wo-dp-alloc-body">
          <svg viewBox="0 0 88 88" className="wo-dp-donut">
            {donut.map((d) => <path key={d.label} d={arc(d.start, d.end)} stroke={d.color} />)}
            <text x="44" y="47" textAnchor="middle">${(value / 1000).toFixed(1)}K</text>
          </svg>
          <ul>
            {ALLOC.map((a) => <li key={a.label}><i style={{ background: a.color }} />{a.label}<b>{Math.round(a.pct * 100)}%</b></li>)}
          </ul>
        </div>
      </div>

      <div className="wo-dp-card wo-dp-pos" style={{ '--i': 7 }}>
        <div className="wo-dp-head"><strong>Open positions</strong><span className="wo-dp-live"><i />Live</span></div>
        <ul>
          {prices.map((p) => {
            const chg = (p.now - p.price) / p.price;
            return (
              <li key={p.sym}>
                <i style={{ background: p.color }}>{p.sym.slice(0, 1)}</i>
                <strong>{p.sym}</strong>
                <span>{p.qty} @ {p.now >= 1000 ? Math.round(p.now).toLocaleString() : p.now.toFixed(2)}</span>
                <b key={tick} className={`wo-dp-flash ${chg >= 0 ? 'is-up' : 'is-down'}`}>{chg >= 0 ? '+' : ''}{(chg * 100).toFixed(2)}%</b>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="wo-dp-card wo-dp-trades" style={{ '--i': 8 }}>
        <div className="wo-dp-head"><strong>Recent trades</strong><span>all accounts</span></div>
        <div className="wo-dp-feed">
          <ul>
            {[...TRADES, ...TRADES].map(([sym, side, pnl], i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}><strong>{sym}</strong><span>{side}</span><b className={pnl.startsWith('-') ? 'is-down' : 'is-up'}>{pnl}</b></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
